import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useAppSelector } from "app/hooks";
import { selectCurrentUser } from "features/auth/auth-slice";
import { wantsCallAlert } from "features/calls/dialstack/call-alerts.util";
import { selectMissedCallFrames } from "features/calls/dialstack/missed-calls.slice";
import useVoiceSeat from "hooks/use-voice-seat.hook";

import { useBrowserNotifications } from "./browser-notifications.context";

// The browser banner for a missed inbound call at the practice (ENG-2476),
// driven by the backend's `call.missed` websocket frame the moment the call
// ends. Same audience as the mobile push: a user with a Voice seat who has
// not switched missed-call alerts off on their account
// (`missed_call_notifications_enabled`, true unless the backend says
// otherwise), plus this computer's own preference, narrowed to the clinics
// they picked under "Alert me about calls to" (ENG-2513). The delivery ledger
// is keyed by call id, so the minute-poll fallback in
// voice-history-notifications.component.tsx cannot banner the same call twice.
export default function MissedCallNotifications() {
  const hasVoiceSeat = useVoiceSeat();
  const user = useAppSelector(selectCurrentUser);
  const { permission, preferences } = useBrowserNotifications();
  if (
    !hasVoiceSeat ||
    permission !== "granted" ||
    !preferences.missedCalls ||
    user?.missed_call_notifications_enabled === false
  )
    return null;
  return <MissedCallObserver />;
}

function MissedCallObserver() {
  const frames = useAppSelector(selectMissedCallFrames);
  const user = useAppSelector(selectCurrentUser);
  const { notify } = useBrowserNotifications();
  const navigate = useNavigate();
  // Frames from before this observer existed were handled by the poll, or
  // belong to a practice/session this one has moved on from.
  const since = useRef(Date.now());
  const seen = useRef(new Set<number>());

  useEffect(() => {
    for (const frame of frames) {
      if (frame.receivedAt < since.current || seen.current.has(frame.callId))
        continue;
      seen.current.add(frame.callId);
      // The caller left a message: "New voicemail" says it better, and that
      // alert arrives with the voicemail itself.
      if (frame.status === "voicemail") continue;
      // A location this user chose not to hear about.
      if (!wantsCallAlert(user, frame.clinicId)) continue;
      notify({
        type: "missedCalls",
        id: String(frame.callId),
        title: "Missed call",
        body: frame.callerLabel,
        onClick: () => navigate(`/calls?call=${frame.callId}`)
      });
    }
  }, [frames, notify, navigate, user]);
  return null;
}
